import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Disc3, Instagram, Linkedin, Music2, Radio, Twitter } from "lucide-react";

import portrait from "@/assets/luiza-portrait-bw.webp.asset.json";
import worksGif from "@/assets/og-projects.gif.asset.json";

export const Route = createFileRoute("/links")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://www.luizajesusdoprado.com/links" },
      { title: "Links — Luiza Jesus do Prado" },
      {
        name: "description",
        content:
          "All links from Luiza Jesus do Prado in one place: works, radio, guided meditation, music, writing, research and contact.",
      },
      { property: "og:title", content: "Links — Luiza Jesus do Prado" },
      { property: "og:description", content: "Every link in one place." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: Links,
});

const internal = [
  { label: "Art", meta: "luizajesusdoprado.com", href: "https://www.luizajesusdoprado.com" },
  { label: "Performance (MAVRA)", meta: "mavrayvyuna.com", href: "https://www.mavrayvyuna.com" },
  { label: "Photography", meta: "luizaprado.com", href: "https://www.luizaprado.com" },
  { label: "Design / Communication", meta: "luizaprado.cc", href: "https://www.luizaprado.cc" },
  { label: "Oculto", meta: "Project", href: "https://www.oculto.management/artists/luiza-jesus-do-prado" },
];

const socials = [
  {
    label: "Spotify — Hifa Cybe",
    href: "https://open.spotify.com/intl-pt/artist/0NzpT3pNycVS6uoFKGUk7R",
    Icon: Music2,
  },
  {
    label: "Spotify — Luiza Prado",
    href: "https://open.spotify.com/intl-pt/artist/3pNVBkJAivjuYrz8gcA4S9",
    Icon: Disc3,
  },
  {
    label: "Bandcamp",
    href: "https://flaviagoa.bandcamp.com/track/flavia-goa-hifa-cybe-correndo-contra-o-tempo",
    Icon: Radio,
  },
  { label: "Instagram", href: "https://www.instagram.com/luizajesusdoprado", Icon: Instagram },
  { label: "Instagram — MAVRA", href: "https://www.instagram.com/mavrayvyuna", Icon: Instagram },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/luizajesusdoprado", Icon: Linkedin },
  { label: "Twitter", href: "https://twitter.com/luizajdoprado", Icon: Twitter },
];

type Material = {
  label: string;
  meta: string;
  href: string;
};

const materials: Material[] = [
  {
    label: "General Portfolio · EN",
    meta: "PDF ↗",
    href: "https://www.luizajesusdoprado.com/midia/Luiza-Jesus-do-Prado_Portfolio_EN.pdf",
  },
  {
    label: "Portfólio Geral · PT",
    meta: "PDF ↗",
    href: "https://www.luizajesusdoprado.com/midia/Luiza-Jesus-do-Prado_Portfolio_PT.pdf",
  },
  {
    label: "MAVRA Portfolio · EN",
    meta: "PDF ↗",
    href: "https://www.luizajesusdoprado.com/midia/Luiza-Jesus-do-Prado_MAVRA-Portfolio_EN.pdf",
  },

  {
    label: "Press Kit",
    meta: "PDFs · Dropbox ↗",
    href: "https://www.dropbox.com/scl/fo/nb4xxuxrbp5gvpd3ff3m7/ALFnfleODqalL4g_pSVy3dA?rlkey=loeygji6h65wyxmv1t3iajn5v&dl=0",
  },
  {
    label: "Media Kit",
    meta: "PDFs · Dropbox ↗",
    href: "https://www.dropbox.com/scl/fo/51ss2svnn5na1i33qw8gp/AOFi8PxvvcPUGzmdqtR3vAo?rlkey=wg5i2jmelodxzntn6u8xp1v65&dl=0",
  },
  {
    label: "Curriculums",
    meta: "PDFs · Dropbox ↗",
    href: "https://www.dropbox.com/scl/fo/accdnbgsole8zfte4vzr6/AJ74uxax2lvH_f1PUuLIgJU?rlkey=irtbx4o89h7cszhgemnakg30g&dl=0",
  },
  {
    label: "CV · EN",
    meta: "PDF ↗",
    href: "https://www.luizajesusdoprado.com/midia/Luiza-Jesus-do-Prado_CV_EN.pdf",
  },
  {
    label: "Currículo · PT",
    meta: "PDF ↗",
    href: "https://www.luizajesusdoprado.com/midia/Luiza-Jesus-do-Prado_CV_PT.pdf",
  },

];

const row =
  "group flex flex-col items-center gap-1 border-b border-bone/12 py-4 text-center transition-colors";

function RotatingName() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % 3), 4200);
    return () => clearInterval(t);
  }, []);
  return (
    <h1
      key={i}
      className="mt-6 animate-[links-fade-in_1.8s_ease-in-out] font-display text-[clamp(1.4rem,5vw,2.2rem)] font-light uppercase leading-[0.95] tracking-[0.02em]"
    >
      {i === 0 && "Luiza Jesus do Prado"}
      {i === 1 && (
        <span>
          <span className="text-bone/65">J</span>
          ESU
          <span className="text-bone/65 line-through decoration-bone/35 decoration-[0.06em]">S</span>
        </span>
      )}
      {i === 2 && "MAVRA"}
    </h1>
  );
}


function Links() {
  return (
    <div className="relative min-h-screen overflow-hidden bg-ink text-bone">
      {/* PORTRAIT — small, centred */}
      <section className="relative flex flex-col items-center px-6 pt-16 text-center">
        <img
          src={portrait.url}
          alt="Luiza Jesus do Prado"
          className="h-32 w-32 rounded-full object-cover object-top grayscale md:h-36 md:w-36"
        />
        <RotatingName />
        <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.3em] text-red">
          Artist · Researcher · Scientist
        </p>
      </section>

      {/* WORKS GIF — small, centred */}
      <Link to="/works" className="group mx-auto mt-10 block w-full max-w-[420px] px-6">
        <div className="relative aspect-[16/9] w-full overflow-hidden border border-bone/12">
          <img
            src={worksGif.url}
            alt="Selected works"
            className="h-full w-full object-cover opacity-80 transition-opacity duration-700 group-hover:opacity-100"
          />
          <span className="absolute bottom-3 left-1/2 -translate-x-1/2 font-mono text-[9px] uppercase tracking-[0.3em] text-bone">
            Selected works ↗
          </span>
        </div>
      </Link>

      {/* LINKS */}
      <main id="main-content" className="mx-auto max-w-[420px] px-6 pb-24 pt-14">
        <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-bone/20 text-center">
          Materials
        </p>
        <div className="mt-4 border-t border-bone/12">
          {materials.map((m) => {
            const inner = (
              <>
                <span className="font-display text-[clamp(1rem,3vw,1.35rem)] font-light uppercase leading-none tracking-[0.02em] text-bone/70 transition-colors group-hover:text-bone">
                  {m.label}
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-bone/20">
                  {m.meta}
                </span>
              </>
            );
            return (
              <a
                key={m.label}
                href={m.href}
                target="_blank"
                rel="noreferrer"
                className={row}
              >
                {inner}
              </a>
            );
          })}
        </div>

        <p className="mt-16 font-mono text-[9px] uppercase tracking-[0.3em] text-bone/20 text-center">Site</p>
        <div className="mt-4 border-t border-bone/12">
          {internal.map((l) => (
            <a
              key={l.label}
              href={l.href}
              {...(l.href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
              className={row}
            >
              <span className="font-display text-[clamp(1rem,3vw,1.35rem)] font-light uppercase leading-none tracking-[0.02em] text-bone/70 transition-colors group-hover:text-bone">
                {l.label}
              </span>
              <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-bone/20">
                {l.meta}
              </span>
            </a>
          ))}
        </div>

        <p className="mt-16 text-center font-mono text-[9px] uppercase tracking-[0.3em] text-bone/20">
          Elsewhere
        </p>
        <div className="mt-5 flex items-center justify-center gap-6">
          {socials.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noreferrer"
              aria-label={s.label}
              title={s.label}
              className="text-bone/65 transition-colors hover:text-bone"
            >
              <s.Icon className="h-[18px] w-[18px]" strokeWidth={1.2} />
            </a>
          ))}
        </div>


        <p className="mt-14 text-center font-mono text-[9px] uppercase tracking-[0.24em] text-bone/20">
          <a href="mailto:luizajesusdoprado@gmail.com" className="hover:text-bone">
            luizajesusdoprado@gmail.com
          </a>
        </p>
      </main>
    </div>
  );
}
